import { useState } from "react";
import { EditIcon } from "../../atoms/icons/EditIcon";
import { theme } from "../../../theme";
import CircleButton from "../../atoms/buttons/CircleButton";
import Modal from "../Modal";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";
import { getInvoiceDistributionTypeTranslationKey } from "../../../utils/invoicesHelpers";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import type { DistributionType } from "../../../models/InvoiceModels";
import { useUpdateInvoiceRecipient } from "../../../graphql/hooks/invoices/useUpdateInvoiceRecipient";

interface Props {
  invoiceId: string;
  currentDistributionType?: DistributionType;
  buttonType: "circleOutlined" | "circleOutlinedSmall";
  disabled?: boolean;
}

const UpdateInvoiceDistributionMethodButton: React.FC<Props> = ({
  invoiceId,
  currentDistributionType,
  buttonType,
  disabled,
}) => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDistributionType, setSelectedDistributionType] = useState<
    DistributionType | undefined
  >(currentDistributionType);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onCompleted = (res?: any) => {
    if (res?.invoice?.updateInvoiceRecipient?.success) {
      setModalOpen(false);
    } else {
      setErrorMessage(t("errors.updateDistributionTypeError"));
    }
  };
  const onError = (error: any) => {
    setErrorMessage(error?.message || t("errors.updateDistributionTypeError"));
  };

  const { updateInvoiceRecipient, error, loading } = useUpdateInvoiceRecipient({
    onCompleted,
    onError,
  });

  const onClickUpdate = async () => {
    setErrorMessage(null);
    try {
      await updateInvoiceRecipient({
        variables: {
          input: {
            invoiceId,
            distributionType: selectedDistributionType,
          },
        },
      });
    } catch (e) {
      setErrorMessage(e?.toString() || t("errors.updateDistributionTypeError"));
    }
  };

  return (
    <div>
      {buttonType === "circleOutlined" && (
        <CircleButton
          icon={<EditIcon color={theme.colors.primary} />}
          onClick={() => setModalOpen(!modalOpen)}
          disabled={disabled}
        />
      )}
      {buttonType === "circleOutlinedSmall" && (
        <CircleButton
          icon={<EditIcon color={theme.colors.primary} />}
          onClick={() => setModalOpen(!modalOpen)}
          disabled={disabled}
          size="small"
        />
      )}

      {modalOpen && (
        <Modal
          closeIcon
          title={t("invoices.selectDistributionType")}
          onClose={() => setModalOpen(false)}
          actionButtons={{
            actionButton: {
              buttonTitle: t("common.save"),
              loading: loading,
              onClick: onClickUpdate,
            },
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setModalOpen(false),
            },
          }}
        >
          <BodySmall>{t("invoices.selectDistributionType")}</BodySmall>
          <HorizontalSpacer spacing={0.5} />

          <div
            style={{
              display: "flex",
              gap: 32,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <RadioButtonBox
              title={t(getInvoiceDistributionTypeTranslationKey("EINVOICE"))}
              small
              checked={selectedDistributionType === "EINVOICE"}
              onChange={() => setSelectedDistributionType("EINVOICE")}
            />
            <RadioButtonBox
              title={t(getInvoiceDistributionTypeTranslationKey("EMAIL"))}
              small
              checked={selectedDistributionType === "EMAIL"}
              onChange={() => setSelectedDistributionType("EMAIL")}
            />
            <RadioButtonBox
              title={t(getInvoiceDistributionTypeTranslationKey("PRINT"))}
              small
              checked={selectedDistributionType === "PRINT"}
              onChange={() => setSelectedDistributionType("PRINT")}
            />
          </div>

          {(errorMessage || error) && (
            <Alert
              type="error"
              title={t("errors.updateDistributionTypeError")}
              message={errorMessage || error?.message}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default UpdateInvoiceDistributionMethodButton;
